import React, { createContext, useEffect, useState, useRef, useCallback } from "react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import axios from "axios";
import {getCoaches, getCurrentUser} from "../api/userApi";
import { useDispatch } from "react-redux";
import { setUser } from "@/slices/userSlice";
import { AppDispatch } from "@/redux/store";
import ClipLoader from "react-spinners/ClipLoader";
import {setOrganisation, setPaymentGateways} from "@/slices/organisationSlice";
import {submitLogout} from "@/api/logoutAPI";
import {getCurrentOrganisation} from "@/api/organisationApi.tsx";
import {fetchServices} from "@/api/serviceApi.tsx";
import {getAllPaymentGateways} from "@/api/PaymentGatewayApi.tsx";
import {getFacilitiesForOrganisation} from "@/api/facilityApi.tsx";
import {setServices} from "@/slices/serviceSlice.ts";
import {setFacilities} from "@/slices/facilitySlice.ts";
import {fetchPlayers} from "@/api/playerApi.tsx";
import {setPlayers} from "@/slices/playerSlice.ts";
import {setCoaches} from "@/slices/coachesSlice.ts";
import {fetchAllEquipment} from "@/api/equipmentApi.tsx";
import {setEquipment} from "@/slices/equipmentSlice.ts";
import {fetchPackages} from "@/api/packageApi.tsx";
import {setPackages} from "@/slices/packagesSlice.ts";
import { getCookie } from "@/utils/cookieUtils";
import i18n from "@/translations/i18n";

// Utility function to check if current route is public
const isPublicRoute = (): boolean => {
    const currentPath = window.location.pathname;
    const publicRoutes = ['/login', '/privacy-policy'];
    return publicRoutes.some(route => currentPath.startsWith(route));
};

interface User {
    token: string;
}

interface ImpersonationInfo {
    targetEmail: string;
    targetName?: string;
    adminEmail: string;
}

interface AuthContextType {
    user: User | null;
    login: () => Promise<void>;
    logout: () => void;
    initializing: boolean;
    setUserState: (user: User | null) => void;
    refreshToken: () => Promise<boolean>;
    isImpersonating: boolean;
    impersonation: ImpersonationInfo | null;
    startImpersonation: (jwt: string, info: ImpersonationInfo) => Promise<void>;
    exitImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    login: async () => {},
    logout: () => {},
    initializing: true,
    setUserState: () => {},
    refreshToken: async () => false,
    isImpersonating: false,
    impersonation: null,
    startImpersonation: async () => {},
    exitImpersonation: async () => {},
});

const ADMIN_TOKEN_KEY = 'jwt_token_admin';
const IMPERSONATION_INFO_KEY = 'impersonation_info';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUserState] = useState<User | null>(null);
    const [initializing, setInitializing] = useState(true);
    const [logoutLoading, setLogoutLoading] = useState(false);
    const [error] = useState<string | null>(null);
    const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(() => {
        try {
            const raw = localStorage.getItem(IMPERSONATION_INFO_KEY);
            return raw ? (JSON.parse(raw) as ImpersonationInfo) : null;
        } catch {
            return null;
        }
    });
    const dispatch = useDispatch<AppDispatch>();
    const refreshTimerRef = useRef<number | null>(null);

    const isTokenValid = (token: string): boolean => {
        try {
            const decoded = jwtDecode<JwtPayload>(token);
            if (!decoded.exp) {
                return false;
            }
            const currentTime = Math.floor(Date.now() / 1000);
            return decoded.exp > currentTime;
        } catch (error) {
            console.error("Invalid token:", error);
            return false;
        }
    };

    const getTokenExpirationTime = (token: string): number => {
        try {
            const decoded = jwtDecode<JwtPayload>(token);
            return decoded.exp || 0;
        } catch (error) {
            console.error("Error decoding token:", error);
            return 0;
        }
    };

    const loadAppData = useCallback(async () => {
        let userDataLoaded = false;
        
        try {
            // Calls run in parallel
            const results = await Promise.allSettled([
                getCurrentUser(),
                getCoaches(),
                getCurrentOrganisation(),
                fetchServices(),
                fetchAllEquipment(),
                getAllPaymentGateways(),
                getFacilitiesForOrganisation(),
                fetchPlayers(),
                fetchPackages(),
            ]);

            // Destructure results to clearly namex them
            const [
                userResult,
                coachesResult,
                orgResult,
                servicesResult,
                equipmentResult,
                paymentGatewaysResult,
                facilityResults,
                playersResult,
                packagesResult,
            ] = results;

            if (userResult.status === "fulfilled") {
                // Validate that the returned user is not the "edun" user or unexpected user
                const userData = userResult.value;
                if (userData && userData.id && 
                    userData.email && userData.email !== "edun" &&
                    userData.firstname !== "edun") { // Removed the requirement for firstname to exist
                    dispatch(setUser(userData));
                    // Pin the UI language to the user's saved profile setting.
                    // i18next otherwise only reads localStorage["locale"], which
                    // can be missing/stale (e.g. after a user clears site data to
                    // pick up a new version, or on a different browser/machine) —
                    // causing the UI to fall back to English despite the profile.
                    const savedLang = userData.settings?.lang;
                    if (
                        (savedLang === "sv_SE" || savedLang === "en_US") &&
                        i18n.language !== savedLang
                    ) {
                        i18n.changeLanguage(savedLang);
                    }
                    userDataLoaded = true;
                } else {
                    console.error("Invalid user data received:", userData);
                    // Don't update user state with invalid data
                }
            } else {
                console.error("User fetch failed:", userResult.reason);
            }

            if (coachesResult.status === "fulfilled") {
                dispatch(setCoaches(coachesResult.value));
            } else {
                console.error("Coaches fetch failed:", coachesResult.reason);
            }

            if (orgResult.status === "fulfilled") {
                dispatch(setOrganisation(orgResult.value));
            } else {
                console.error("Organisation fetch failed:", orgResult.reason);
            }

            if (servicesResult.status === "fulfilled") {
                dispatch(setServices(servicesResult.value));
            } else {
                console.error("Services fetch failed:", servicesResult.reason);
            }

            if (equipmentResult.status === "fulfilled") {
                dispatch(setEquipment(equipmentResult.value));
            } else {
                console.error("Equipment fetch failed:", equipmentResult.reason);
            }

            if (paymentGatewaysResult.status === "fulfilled") {
                const providers: string[] = paymentGatewaysResult.value || [];
                const gateways = providers.map((p) => ({ Provider: p }));
                dispatch(setPaymentGateways(gateways));
            } else {
                console.error("PaymentGateways fetch failed:", paymentGatewaysResult.reason);
            }

            if (facilityResults.status === "fulfilled") {
                dispatch(setFacilities(facilityResults.value));
            } else {
                console.error("Facilities fetch failed:", facilityResults.reason);
            }

            if (playersResult.status === "fulfilled") {
                dispatch(setPlayers(playersResult.value));
            } else {
                console.error("Players fetch failed:", playersResult.reason);
            }

            if (packagesResult.status === "fulfilled") {
                dispatch(setPackages(packagesResult.value));
            } else {
                console.error("Packages fetch failed:", packagesResult.reason);
            }

        } catch (err) {
            console.error("Unexpected error in loadAppData:", err);
        } finally {
            // Only set initializing to false if user data was successfully loaded
            // This ensures that components don't render until user data is available
            if (userDataLoaded) {
                setInitializing(false);
            } else {
                // If user data failed to load, we should still set initializing to false
                // to prevent infinite loading, but log the issue
                console.error("Failed to load user data, but setting initializing to false to prevent infinite loading");
                setInitializing(false);
            }
        }
    }, [dispatch]);

    const refreshToken = useCallback(async (): Promise<boolean> => {
        // Check if we're on a public route - don't attempt refresh
        if (isPublicRoute()) {
            console.log("Skipping token refresh on public route");
            return false;
        }

        // once at app startup (e.g., api.ts)
        axios.defaults.withCredentials = true;
        axios.defaults.xsrfCookieName = 'csrf_token';
        axios.defaults.xsrfHeaderName = 'X-CSRF-Token';

        const goApiBase = import.meta.env.VITE_GO_API_BASE_URL || import.meta.env.VITE_GO_API_URL;
        const RENEW_TIMEOUT_MS = 60000; // 60s - Cloud Run cold start can take 30-60s
        const MAX_RETRIES = 3;
        const RETRY_DELAY_MS = 2000;

        const doRenew = async (): Promise<{ jwt_token?: string } | null> => {
            const response = await axios.post(
                goApiBase + `/go-api/auth/renew_jwt`,
                {},
                {
                    params: { ts: Date.now() },
                    headers: { 'X-CSRF-Token': getCookie('csrf_token') ?? '' },
                    withCredentials: true,
                    timeout: RENEW_TIMEOUT_MS,
                }
            );
            return response.data;
        };

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                const data = await doRenew();
                const newToken = data?.jwt_token;
                const msg = (data as { message?: string })?.message ?? "";
                // Backend may return 200 with empty jwt_token + "CSRF validation failed"
                if (!newToken && msg.toLowerCase().includes("csrf")) {
                    console.warn("CSRF validation failed - session may have expired (e.g. backend restarted)");
                    setUserState(null);
                    localStorage.removeItem("jwt_token");
                    return false;
                }
                if (newToken) {
                    localStorage.setItem('jwt_token', newToken);
                    setUserState({ token: newToken });
                    try { await loadAppData(); } catch (e) { console.error('Reload failed:', e); }
                    setupTokenRefreshTimer(newToken);
                    return true;
                }
            } catch (error: any) {
                const status = error?.response?.status;
                const backendMessage: string | undefined = error?.response?.data?.message;
                const isNetworkOrTimeout =
                    !error?.response &&
                    (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK' || error?.message?.includes('timeout'));

                // Handle explicit deleted/inactive user - never retry
                if (status === 401 && backendMessage?.includes("Unauthorized: User not found")) {
                    try {
                        localStorage.removeItem('jwt_token');
                        setUserState(null);
                    } finally {
                        try {
                            sessionStorage.setItem(
                                'loginNotice',
                                JSON.stringify({ type: 'inactive' })
                            );
                        } catch (_) {}
                        window.location.replace('/login');
                    }
                    return false;
                }

                // 400/403 = no valid session or CSRF failed - don't retry
                const msg = error?.response?.data?.message ?? "";
                if (status === 400 || status === 403 || msg.toLowerCase().includes("csrf")) {
                    console.log("Token refresh failed (invalid session or CSRF). Clearing user state.");
                    setUserState(null);
                    localStorage.removeItem("jwt_token");
                    return false;
                }

                // Retry on network/timeout (e.g. Cloud Run cold start)
                if (isNetworkOrTimeout && attempt < MAX_RETRIES) {
                    console.warn(`Token refresh attempt ${attempt}/${MAX_RETRIES} failed (backend may be waking up), retrying in ${RETRY_DELAY_MS}ms...`, error?.message || error);
                    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
                    continue;
                }

                console.error("Failed to refresh token:", error);
                return false;
            }
        }

        return false;
    }, [loadAppData]);

    const setupTokenRefreshTimer = useCallback((token: string) => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

        // While impersonating, the refresh_token cookie still belongs to the admin,
        // so renew_jwt would silently restore the admin session. Skip the timer.
        if (localStorage.getItem(ADMIN_TOKEN_KEY)) return;

        const exp = getTokenExpirationTime(token);
        if (!exp) return;

        const refreshAt = (exp - 300) * 1000;      // 5 min before exp (ms)
        localStorage.setItem('next_refresh_at', String(refreshAt));

        refreshTimerRef.current = window.setTimeout(refreshToken, refreshAt - Date.now());
    }, [refreshToken]);

    const handleVisibilityChange = useCallback(async () => {
        if (document.visibilityState !== 'visible') return;

        // Don't attempt token refresh on public routes
        if (isPublicRoute()) {
            return;
        }

        // Mid-impersonation: refresh_token cookie still belongs to the admin,
        // so renew_jwt would silently restore the admin and break the session.
        if (localStorage.getItem(ADMIN_TOKEN_KEY)) {
            return;
        }

        const now = Date.now();
        const refreshAt = Number(localStorage.getItem('next_refresh_at') || 0);

        if (now >= refreshAt) {
            // token is about to / already expired → try refresh
            if (!(await refreshToken())) {
                // still failed?  fall back to log-out logic here…
            }
        }
    }, [refreshToken]);

    const checkTokens = useCallback(async () => {
        const idToken = localStorage.getItem("jwt_token");

        if (idToken && isTokenValid(idToken)) {
            setUserState({ token: idToken });
            await loadAppData();
            setupTokenRefreshTimer(idToken);
        } else {
            const refreshed = await refreshToken();
            if (!refreshed) {
                setUserState(null);
            }
            setInitializing(false);
        }
    }, [loadAppData, setupTokenRefreshTimer]);

    const login = async () => {
        // After login, always load app data regardless of current path
        const idToken = localStorage.getItem("jwt_token");
        if (idToken && isTokenValid(idToken)) {
            setUserState({ token: idToken });
            await loadAppData(); // loadAppData will handle setting initializing to false
            setupTokenRefreshTimer(idToken);
        } else {
            await checkTokens();
        }
    };

    const startImpersonation = useCallback(async (jwt: string, info: ImpersonationInfo) => {
        // Save the real admin token (if not already saved from a previous attempt)
        // so exit can restore the session without going through Supabase again.
        if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
            const current = localStorage.getItem('jwt_token');
            if (current) localStorage.setItem(ADMIN_TOKEN_KEY, current);
        }
        // Pause the auto-refresh timer — renew_jwt would issue a fresh admin
        // token and clobber the impersonation session.
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        localStorage.setItem('jwt_token', jwt);
        localStorage.setItem(IMPERSONATION_INFO_KEY, JSON.stringify(info));
        setImpersonation(info);
        setUserState({ token: jwt });
        await loadAppData();
    }, [loadAppData]);

    const exitImpersonation = useCallback(async () => {
        const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATION_INFO_KEY);
        setImpersonation(null);
        if (adminToken && isTokenValid(adminToken)) {
            localStorage.setItem('jwt_token', adminToken);
            setUserState({ token: adminToken });
            await loadAppData();
            setupTokenRefreshTimer(adminToken);
        } else {
            // Saved admin token expired — fall back to a fresh refresh, which uses
            // the admin's still-valid refresh-token cookie to mint a new JWT.
            await refreshToken();
        }
    }, [loadAppData, refreshToken, setupTokenRefreshTimer]);

    const logout = async () => {
        setLogoutLoading(true);
        try {
            // Clear the refresh timer
            if (refreshTimerRef.current) {
                window.clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            await submitLogout();
            localStorage.removeItem("jwt_token");
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            localStorage.removeItem(IMPERSONATION_INFO_KEY);
            setImpersonation(null);
            dispatch(setUser(null));
            setUserState(null);
        } catch (error) {
            console.error("Logout failed:", error);
        } finally {
            setLogoutLoading(false);
        }
    };

    // Handle visibility change to refresh token when app comes back to foreground
    useEffect(() => {
        // Add event listeners for both visibility change and focus
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleVisibilityChange);
            // Clear the refresh timer when component unmounts
            if (refreshTimerRef.current) {
                window.clearTimeout(refreshTimerRef.current);
            }
        };
    }, [handleVisibilityChange]);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // Only check tokens if we're not on a public route
                if (!isPublicRoute()) {
                    await checkTokens();
                } else {
                    // For public routes, just check if there's a valid token without refreshing
                    const idToken = localStorage.getItem("jwt_token");
                    if (idToken && isTokenValid(idToken)) {
                        setUserState({ token: idToken });
                        await loadAppData();
                        setupTokenRefreshTimer(idToken);
                    } else {
                        setInitializing(false);
                    }
                }
            } catch (error) {
                console.error("Error during initialization:", error);
                setInitializing(false);
            }
        };
        initializeAuth();
    }, [checkTokens, loadAppData, setupTokenRefreshTimer]);

    if (logoutLoading || initializing) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <ClipLoader color="#64BE78" size={50} />
                    <p className="mt-4 text-lg font-semibold text-gray-700">
                        {logoutLoading ? "Logging out..." : "Loading user data..."}
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <p className="text-lg font-semibold text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={{
            user,
            login,
            logout,
            initializing,
            setUserState,
            refreshToken,
            isImpersonating: impersonation !== null,
            impersonation,
            startImpersonation,
            exitImpersonation,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
